/**
 * The role model and which console pages each role sees.
 *
 * This only HIDES what a role cannot use. The server enforces every rule again on every
 * request, so nothing here is a security boundary. The role always comes from the
 * signed-in session, never from a URL or a client setting.
 *
 * Sources: the Console V3 design's canSee() table (WHO-SEES-WHAT), the backend handoff's
 * role sections ("Aithora staff roles", "System Pulse: who sees it", Settings 10g) and
 * src/aithora/role-grants.ts for the role names the server stores.
 */

/** Every role the server stores in users.role. */
export const ROLES = [
  'SUPER_ADMIN',
  'OPERATOR',
  'SALES',
  'DEVELOPER',
  'PARTNER',
  'ADMIN',
  'CAREGIVER',
  'STAFF',
  'RESIDENT',
  'FAMILY_MEMBER',
  'MEMBER',
] as const;

export type Role = (typeof ROLES)[number];

/** Aithora's own team (they sign in with no facility). */
export const TEAM_ROLES: ReadonlySet<Role> = new Set<Role>(['SUPER_ADMIN', 'OPERATOR', 'SALES', 'DEVELOPER', 'PARTNER']);
/** Care Staff in the design: two stored roles, one console persona. */
export const CARE_STAFF_ROLES: ReadonlySet<Role> = new Set<Role>(['CAREGIVER', 'STAFF']);

/**
 * Normalise whatever the server sent into a known role, or null. Unknown roles
 * (including OWNER, which is never a stored role) get null and therefore see nothing.
 */
export function toRole(raw: unknown): Role | null {
  const r = String(raw ?? '').trim().toUpperCase();
  return (ROLES as readonly string[]).includes(r) ? (r as Role) : null;
}

/** The name people see for each role (the design's picker names). */
export const ROLE_LABEL: Record<Role, string> = {
  SUPER_ADMIN: 'Super Admin',
  OPERATOR: 'Operator',
  SALES: 'Sales',
  DEVELOPER: 'Dev',
  PARTNER: 'Partner',
  ADMIN: 'Facility Admin',
  CAREGIVER: 'Care Staff',
  STAFF: 'Care Staff',
  RESIDENT: 'Resident',
  FAMILY_MEMBER: 'Family',
  MEMBER: 'Aithora Home',
};

/** Console pages, in side-nav order. */
export type ViewId =
  | 'overview'
  | 'home'
  | 'companion'
  | 'residents'
  | 'guardian'
  | 'worklist'
  | 'family'
  | 'pulse'
  | 'oversight'
  | 'atlas'
  | 'policy'
  | 'finance'
  | 'capture'
  | 'performance'
  | 'atlasIntel'
  | 'sales'
  | 'settings'
  | 'help';

/** A /nav/badges key (the server's names). */
export type BadgeKey =
  | 'residents'
  | 'worklist'
  | 'family'
  | 'oversight'
  | 'policy'
  | 'capture'
  | 'settings'
  | 'guardian'
  | 'companion'
  /** PROPOSED (not served by /nav/badges yet): System Pulse's safety status ("Outage"). */
  | 'pulse';

export interface NavItem {
  view: ViewId;
  label: string;
  /** URL path. A path picks a page; it never picks a role or a facility. */
  path: string;
  /** Side-nav group. A small gap separates groups, as in the design. */
  group: 1 | 2 | 3 | 4;
  /** Which /nav/badges key drives this item's status label, if any. */
  badge?: BadgeKey;
  /**
   * 'status' draws a dot and a word (Guardian, Worklist, Atlas);
   * 'count' draws a bare count or short figure.
   */
  badgeStyle?: 'status' | 'count';
  /** Kept out of the side nav (reached from the top bar). */
  hiddenInNav?: boolean;
}

export const NAV_ITEMS: readonly NavItem[] = [
  { view: 'overview', label: 'Overview', path: '/overview', group: 1 },
  { view: 'home', label: 'Aithora Home', path: '/home', group: 1 },
  // Console V6 retires "Atlas": one assistant, Atlas. This page is the resident's own
  // Atlas (and the Home member's and family's, opened from their pages); staff read
  // residents' conversations in Atlas > Conversations instead.
  // The resident's own Atlas page. Its address is /me: the old /companion address still
  // opens it (LEGACY_PATHS), so saved links and the phone app keep working.
  { view: 'companion', label: 'Atlas', path: '/me', group: 1, badge: 'companion', badgeStyle: 'status' },
  { view: 'residents', label: 'Residents', path: '/residents', group: 1, badge: 'residents', badgeStyle: 'count' },
  { view: 'guardian', label: 'Guardian', path: '/guardian', group: 1, badge: 'guardian', badgeStyle: 'status' },
  { view: 'worklist', label: 'Worklist', path: '/worklist', group: 1, badge: 'worklist', badgeStyle: 'status' },
  { view: 'family', label: 'Family Portal', path: '/family', group: 1, badge: 'family', badgeStyle: 'count' },
  { view: 'pulse', label: 'System Pulse', path: '/pulse', group: 2, badge: 'pulse', badgeStyle: 'status' },
  { view: 'oversight', label: 'Oversight', path: '/oversight', group: 2, badge: 'oversight', badgeStyle: 'count' },
  { view: 'atlas', label: 'Atlas', path: '/atlas', group: 2 },
  { view: 'policy', label: 'Policy', path: '/policy', group: 2, badge: 'policy', badgeStyle: 'count' },
  { view: 'finance', label: 'Finance', path: '/finance', group: 3 },
  { view: 'capture', label: 'Capture', path: '/capture', group: 3, badge: 'capture', badgeStyle: 'count' },
  { view: 'performance', label: 'Performance', path: '/performance', group: 3 },
  { view: 'atlasIntel', label: 'Atlas Intelligence', path: '/intelligence', group: 3 },
  { view: 'sales', label: 'Sales', path: '/sales', group: 3 },
  { view: 'settings', label: 'Settings', path: '/settings', group: 4, badge: 'settings', badgeStyle: 'count' },
  { view: 'help', label: 'Help', path: '/help', group: 4, hiddenInNav: true },
];

const ALL_VIEWS: readonly ViewId[] = NAV_ITEMS.map((n) => n.view);
/**
 * Console V6 (canSee: STAFF_CONV): staff no longer open the resident-facing Atlas page; they
 * read residents' conversations in Atlas > Conversations, so it leaves their nav.
 */
const STAFF_VIEWS: readonly ViewId[] = ALL_VIEWS.filter((v) => v !== 'companion');

/**
 * Pages each role may open. Help is open to every role so a role's own guide is
 * always reachable.
 */
const VIEWS_BY_ROLE: Record<Role, readonly ViewId[]> = {
  // Super Admin and Operator see the same pages; Super Admin adds destructive controls
  // inside them (Danger Zone, role changes), not extra pages. In the Family Portal they
  // see the design's read-only Coverage view and nothing else (Andrew, 2026-09-30):
  // Aithora never grants itself family access and never reads a family's private
  // messages, and the server refuses both (v3/family/aithora-guard.ts).
  SUPER_ADMIN: STAFF_VIEWS,
  OPERATOR: STAFF_VIEWS,
  // Partner: backend-only and read-only. The design's Partner lens is the Operator's
  // pages without Aithora Home and System Pulse (Pulse is internal: Operator and Dev),
  // and without the Family Portal (Andrew's Coverage view is for Super Admin and Operator).
  // No Guardian either (Andrew, 2026-10-02): the server refuses it to the Partner.
  // No Sales (Console V4 handoff, Spec V4 step 1: Partner nav hides Guardian, Atlas
  // Intelligence, Sales and System Pulse).
  // Console V5: Atlas Intelligence is Super Admin and Operator only, so not the Partner either.
  PARTNER: STAFF_VIEWS.filter((v) => v !== 'home' && v !== 'pulse' && v !== 'family' && v !== 'guardian' && v !== 'sales' && v !== 'atlasIntel'),
  // "Sales: Sales, Settings (Two-factor and Notifications only) and Help." Andrew,
  // 2026-09-30: Sales keeps Atlas Intelligence; 2026-10-01: Find and the Relationship
  // Map only (the page's SALES_INTEL_TABS; the server's SALES_INTEL_ROUTES).
  // Andrew, 2026-10-13: Sales gets both, Atlas (the design's V5/V6 move) and Atlas
  // Intelligence (finding facilities to sell to). Atlas answers Sales with no resident data
  // and no money (v3/atlas/context.ts persona 'sales'; atlas-access.ts SALES_CAPS).
  SALES: ['atlasIntel', 'atlas', 'sales', 'settings', 'help'],
  // "Dev: System Pulse, Oversight, Policy, Settings and Help." Andrew, 2026-09-30:
  // Developers keep Atlas, as production had (Chat and Eval; no resident PHI).
  // Andrew, 2026-10-16: Developers also read Finance, Capture, Performance and Atlas
  // Intelligence, read-only (DEVELOPER_READ_ONLY_VIEWS), to see where they can help.
  DEVELOPER: ['pulse', 'oversight', 'atlas', 'policy', 'finance', 'capture', 'performance', 'atlasIntel', 'settings', 'help'],
  // Facility Admin: everything except Atlas Intelligence, Sales, Family Portal and
  // Aithora Home. System Pulse and Oversight are scoped to their building by the server.
  ADMIN: STAFF_VIEWS.filter((v) => !['atlasIntel', 'sales', 'family', 'home'].includes(v)),
  CAREGIVER: ['overview', 'residents', 'guardian', 'worklist', 'atlas', 'settings', 'help'],
  STAFF: ['overview', 'residents', 'guardian', 'worklist', 'atlas', 'settings', 'help'],
  RESIDENT: ['companion', 'help'],
  // Andrew, 2026-10-19: everyone gets Atlas. Family open the Atlas page (Chat only), about
  // their own person: the server answers them on the 'family' tier (atlas-access.ts), which
  // reads only their own pages.
  FAMILY_MEMBER: ['family', 'atlas', 'settings', 'help'],
  MEMBER: ['home', 'settings', 'help'],
};

/** May this role open this page? Unknown roles open nothing. */
export function canSee(role: Role | null, view: ViewId): boolean {
  if (!role) return false;
  return VIEWS_BY_ROLE[role].includes(view);
}

/** The side-nav items for a role, in design order. */
export function navFor(role: Role | null): NavItem[] {
  if (!role) return [];
  return NAV_ITEMS.filter((n) => !n.hiddenInNav && canSee(role, n.view));
}

/** Where a role lands after signing in. */
export function homeViewFor(role: Role | null): ViewId | null {
  if (!role) return null;
  // Sales lands on Sales (the design), though Atlas Intelligence sits above it in the nav.
  if (role === 'SALES') return 'sales';
  const first = navFor(role)[0];
  return first ? first.view : canSee(role, 'help') ? 'help' : null;
}

/** Residents use Atlas full-screen: no side nav (design: Resident has no aside). */
export function hasSidebar(role: Role | null): boolean {
  return role !== null && role !== 'RESIDENT';
}

/** Resident and Aithora Home get no bell: Atlas speaks their alerts. */
export function hasBell(role: Role | null): boolean {
  return role !== null && role !== 'RESIDENT' && role !== 'MEMBER';
}

/** The bell's name: Family see "Updates", everyone else "Alerts". */
export function bellLabel(role: Role | null): string {
  return role === 'FAMILY_MEMBER' ? 'Updates' : 'Alerts';
}

/** Partner may read but never write; the UI hides actions, the API rejects them. */
export function isReadOnly(role: Role | null): boolean {
  return role === 'PARTNER';
}

/** The pages a Developer may read but never change (Andrew, 2026-10-16). */
export const DEVELOPER_READ_ONLY_VIEWS: readonly ViewId[] = ['finance', 'capture', 'performance', 'atlasIntel'];

/** Read-only on this page: the Partner everywhere, a Developer on the money and Intelligence pages. */
export function isReadOnlyOn(role: Role | null, view: ViewId | null): boolean {
  if (isReadOnly(role)) return true;
  return role === 'DEVELOPER' && view !== null && DEVELOPER_READ_ONLY_VIEWS.includes(view);
}

export function isTeamRole(role: Role | null): boolean {
  return role !== null && TEAM_ROLES.has(role);
}

/** Old addresses that still open a page whose address changed: old link -> current path. */
export const LEGACY_PATHS: Readonly<Record<string, string>> = { '/companion': '/me' };

/** The view for a URL path, or null when the path names no page. */
export function viewForPath(path: string): ViewId | null {
  const raw = `/${path.replace(/^\/+/, '').split(/[/?#]/)[0]}`;
  const clean = LEGACY_PATHS[raw] ?? raw;
  const hit = NAV_ITEMS.find((n) => n.path === clean);
  return hit ? hit.view : null;
}

export function pathForView(view: ViewId): string {
  const hit = NAV_ITEMS.find((n) => n.view === view);
  return hit ? hit.path : '/';
}

export function labelForView(view: ViewId): string {
  const hit = NAV_ITEMS.find((n) => n.view === view);
  return hit ? hit.label : 'Console';
}
